import { useEffect, useRef, useState } from 'react';
import { Box, TextField } from '@mui/material';

interface GooglePlacesAutocompleteProps {
  label: string;
  value: { city?: string; state?: string; country?: string };
  onChange: (value: { city?: string; state?: string; country?: string }) => void;
  required?: boolean;
}

declare global {
  interface Window {
    google: any;
  }
}

export function GooglePlacesAutocomplete({ value, onChange }: GooglePlacesAutocompleteProps) {
  const autocompleteRef = useRef<HTMLDivElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (value.city || value.state || value.country) {
      const parts = [value.city, value.state, value.country].filter(Boolean);
      setInputValue(parts.join(', '));
    }
  }, [value]);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 50;

    const checkGoogleMaps = () => {
      attempts++;
      if (window.google?.maps?.importLibrary) {
        setIsLoaded(true);
      } else if (attempts >= maxAttempts) {
        console.error('Google Maps failed to load after 5 seconds');
        setLoadError(true);
      } else {
        setTimeout(checkGoogleMaps, 100);
      }
    };
    checkGoogleMaps();
  }, []);

  useEffect(() => {
    if (!isLoaded || !autocompleteRef.current) return;

    const initAutocomplete = async () => {
      try {
        const { BasicPlaceAutocompleteElement } = await window.google.maps.importLibrary('places');

        const autocompleteElement = new BasicPlaceAutocompleteElement();
        autocompleteElement.style.width = '100%';
        autocompleteElement.style.height = '40px';
        autocompleteElement.style.borderRadius = '4px';
        autocompleteElement.style.backgroundColor = 'transparent';
        autocompleteElement.style.colorScheme = 'light';

        autocompleteElement.addEventListener('gmp-select', async (event: any) => {
          const place = event.place;

          if (!place.addressComponents) {
            await place.fetchFields({ fields: ['addressComponents'] });
          }

          const addressComponents = place.addressComponents || [];

          const city = addressComponents.find((c: any) => c.types.includes('locality'))?.longText;
          const state = addressComponents.find((c: any) => c.types.includes('administrative_area_level_1'))?.shortText;
          const country = addressComponents.find((c: any) => c.types.includes('country'))?.longText;

          onChange({
            city: city || undefined,
            state: state || undefined,
            country: country || undefined,
          });
        });

        if (autocompleteRef.current) {
          autocompleteRef.current.innerHTML = '';
          autocompleteRef.current.appendChild(autocompleteElement);

          const input = autocompleteElement.querySelector('input');
          if (input && inputValue) {
            input.value = inputValue;
            input.placeholder = inputValue;
          }
        }
      } catch (error) {
        console.error('Error initializing Google Places Autocomplete:', error);
      }
    };

    initAutocomplete();
  }, [isLoaded]);

  if (loadError || !isLoaded) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="City" value={value.city || ''} onChange={(e) => onChange({ ...value, city: e.target.value || undefined })} size="small" sx={{ flex: 1 }} />
          <TextField label="State" value={value.state || ''} onChange={(e) => onChange({ ...value, state: e.target.value || undefined })} size="small" sx={{ width: 100 }} />
          <TextField label="Country" value={value.country || ''} onChange={(e) => onChange({ ...value, country: e.target.value || undefined })} size="small" sx={{ width: 120 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {inputValue && <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Current: {inputValue}</Box>}
      <Box
        ref={autocompleteRef as any}
        sx={{
          '& gmp-basic-place-autocomplete': {
            width: '100%',
          },
        }}
      />
    </Box>
  );
}
